import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Button,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';

interface DeleteDraftModalProps {
  deleteDraftId?: number;
  onDismiss: () => void;
}

export const DeleteDraftModal: React.FC<DeleteDraftModalProps> = ({ deleteDraftId, onDismiss }) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const deleteDraftMutation = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.deleteDraftEmployee,
    onSuccess: () => {
      toast.show({
        content: 'Draft deleted successfully',
        color: 'information',
      });
      queryClient.invalidateQueries({
        queryKey: ['draft-employees'],
      });
      onDismiss();
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });
  return (
    <Modal
      isOpen={!!deleteDraftId}
      onDismiss={() => {
        if (deleteDraftMutation.isLoading) {
          return;
        }
        onDismiss();
      }}
      size="small"
      accessibilityLabel="Delete Draft">
      <ModalHeader title="Delete Draft" />

      <ModalBody>
        <Text size="medium" color="surface.text.gray.normal">
          Are you sure you want to delete this draft?
        </Text>
      </ModalBody>

      <ModalFooter>
        <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
          <Button variant="tertiary" onClick={onDismiss} isDisabled={deleteDraftMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            color="negative"
            isLoading={deleteDraftMutation.isLoading}
            onClick={() => deleteDraftMutation.mutate(deleteDraftId!)}>
            Delete
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
